/**
 * The switch. Flip to `false` for the plain "Thinking…" line.
 *
 * Deliberately a constant rather than a setting: it is a house-voice decision,
 * not something each user should have to discover and configure. Safe either
 * way, because it only ever replaces the generic placeholder — what a tool is
 * actually doing is always reported as-is.
 */
export const PLAYFUL_THINKING_ENABLED = true;

/**
 * Stand-ins for "Thinking…" while a run has not yet reported what it is doing.
 *
 * These are jokes, and they are only ever shown in place of the generic
 * fallback — never in place of a real `chat_status` frame. The moment the
 * backend says what it is actually doing, that wins: a user waiting on a
 * contract review is owed the truth about tool calls, not a punchline.
 *
 * House rules for adding to this list, so it stays funny rather than annoying:
 * every line is about the *work* (law, contracts, review), none implies real
 * progress that isn't happening, and none is at the user's expense.
 */
export const PLAYFUL_THINKING_MESSAGES: readonly string[] = [
  'Reading every law book ever written',
  'Cramming for the bar exam',
  'Looking up what "notwithstanding" notwithstands',
  'Counting the semicolons in clause 14',
  'Arguing with itself about governing law',
  'Untangling a 400-word sentence',
  'Asking the indemnity clause to explain itself',
  'Checking whether "reasonable efforts" was reasonable',
  'Alphabetising the definitions section',
  'Following a cross-reference into clause 12.3(b)(iv)',
  'Translating legalese into words',
  'Weighing the liability cap',
  'Consulting the playbook, twice',
  'Searching for the missing schedule',
  'Reconciling two definitions of "Affiliate"',
  'Deciding if that comma changes everything',
  'Reading the fine print under the fine print',
  'Colour-coding the redline',
  'Politely disagreeing with opposing counsel',
  'Finding out who "the Parties" are this time',
  'Auditing the termination triggers',
  'Measuring the notice period',
  'Double-checking the effective date',
  'Hunting for an unlimited liability clause',
  'Making sure the annexes actually exist',
];

/**
 * Labels that carry no information, so a quip can stand in for them.
 *
 * The backend brackets model turns in a synthetic status group labelled
 * "Thinking…" (`_runner.py::_open_thinking_group`), which is why the status
 * list is never actually empty while a run is warming up. "Working…" is
 * `useChatStatusGroups`' own stand-in for a group that arrived without a label.
 *
 * Everything else is a real tool reporting real work and is left alone.
 */
const GENERIC_LABELS = new Set(['thinking', 'working']);

export function isGenericThinkingLabel(label: string): boolean {
  // Tolerate the ellipsis in either form, and any casing.
  const normalized = label
    .trim()
    .replace(/(\.{3}|…)$/, '')
    .trim()
    .toLowerCase();
  return GENERIC_LABELS.has(normalized);
}

/**
 * A random line, never the one already showing — a repeat reads like the UI has
 * frozen, which is the opposite of what this is for.
 */
export function nextPlayfulThinkingMessage(current?: string): string {
  const options = PLAYFUL_THINKING_MESSAGES.filter(message => message !== current);
  // `current` not being in the list (or a one-item list) leaves options empty.
  const pool = options.length > 0 ? options : PLAYFUL_THINKING_MESSAGES;
  return pool[Math.floor(Math.random() * pool.length)];
}
